import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';
import {
  type SearchableField,
  type SearchableFieldRow,
} from '../../ISearchableField.tsx';
import { isSearchableFieldRowActive } from '../../isSearchableFieldRowActive.ts';

export interface UseShownFilterNamesOptions {
  /** ID univoco per il key di localStorage `advsrc-${id}`. */
  id: string;
  fields: SearchableField[];
  /** Query corrente in formato `parsedQuery` (post transform.parse). */
  query: Record<string, any>;
  disableLocalStorage?: boolean;
}

/**
 * Legge da localStorage (`advsrc-${id}`) i nomi dei filtri mostrati l'ultima
 * volta e ritorna solo quelli ancora validi: campo tuttora esistente in
 * `fields` e non `hidden`. Fail-soft: ritorna `[]` se la chiave manca, il JSON
 * è corrotto o lo storage non è disponibile.
 */
function readStoredNames(id: string, fields: SearchableField[]): string[] {
  try {
    const raw = localStorage.getItem(`advsrc-${id}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SearchableFieldRow[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(r => r?.name)
      .filter((n): n is string => typeof n === 'string')
      .filter(name => {
        const field = fields.find(f => f.name === name);
        return field != null && !field.hidden;
      });
  } catch {
    return [];
  }
}

function getActiveNames(
  query: Record<string, any>,
  fields: SearchableField[],
): string[] {
  return fields
    .filter(f => isSearchableFieldRowActive(query, f) && !f.hidden)
    .map(f => f.name);
}

/**
 * Hook che gestisce l'elenco dei nomi dei filtri visibili come chip nella
 * FilterBar. Replica il pattern di `AdvancedSearchFormSection` per:
 * - mergiare i nomi attivi (presenti in `query`) con quelli memorizzati in
 *   localStorage (chip placeholder con valore vuoto)
 * - persistere su localStorage con la stessa chiave `advsrc-${id}`
 */
export function useShownFilterNames({
  id,
  fields,
  query,
  disableLocalStorage,
}: Readonly<UseShownFilterNamesOptions>) {
  const [shownFieldNames, setShownFieldNames] = useState<string[]>(() => {
    const stored = disableLocalStorage ? [] : readStoredNames(id, fields);
    const active = getActiveNames(query, fields);
    const seen = new Set(stored);
    return [...stored, ...active.filter(n => !seen.has(n))];
  });

  // Chiave (nomi attivi joinati) dell'ultimo set di filtri attivi processato.
  const prevActiveKeyRef = useRef<string>(
    getActiveNames(query, fields).join(','),
  );
  // Effect 1 — Allinea i chip mostrati ai filtri attivi nella `query`.
  // Quando la query cambia (navigazione URL, setter esterni, edit di un chip)
  // possono comparire filtri valorizzati che non hanno ancora un chip: questo
  // effect li aggiunge alla lista. È SOLO additivo: i chip svuotati restano
  // come placeholder e non vengono mai rimossi qui. Il confronto è per
  // CONTENUTO (via `prevActiveKeyRef`), non per riferimento, perché il consumer
  // ricrea `query` a ogni render e un confronto per identità farebbe scattare
  // l'effect (e il setState) inutilmente.
  useEffect(() => {
    const active = getActiveNames(query, fields);
    const key = active.join(',');
    if (key === prevActiveKeyRef.current) return;
    prevActiveKeyRef.current = key;
    if (active.length === 0) return;
    setShownFieldNames(prev => {
      const seen = new Set(prev);
      const additions = active.filter(n => !seen.has(n));
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
  }, [query, fields]);

  // Effect 2 — Persiste l'elenco dei chip mostrati su localStorage (chiave
  // `advsrc-${id}`), così che la selezione dei filtri sopravviva al reload.
  // Disattivabile via `disableLocalStorage`; errori (quota piena / storage non
  // disponibile) loggati con `console.warn` ma non bloccanti.
  useEffect(() => {
    if (disableLocalStorage) return;
    const rows: SearchableFieldRow[] = shownFieldNames.map(name => ({
      id: v4(),
      name,
    }));
    try {
      localStorage.setItem(`advsrc-${id}`, JSON.stringify(rows));
    } catch (e) {
      console.warn(
        '[useShownFilterNames] Persistenza su localStorage fallita',
        {
          id,
          error: e,
        },
      );
    }
  }, [shownFieldNames, id, disableLocalStorage]);

  const addField = useCallback((name: string) => {
    setShownFieldNames(prev => (prev.includes(name) ? prev : [...prev, name]));
  }, []);

  const removeField = useCallback((name: string) => {
    setShownFieldNames(prev => prev.filter(n => n !== name));
  }, []);

  // Rimuove TUTTI i chip in un colpo solo (usato dal pulsante "Pulisci").
  // L'Effect 2 di persistenza scrive da solo `[]` su localStorage.
  const clearFields = useCallback(() => {
    setShownFieldNames([]);
  }, []);

  return { shownFieldNames, addField, removeField, clearFields };
}
